import { faAnglesLeft, faBars } from '@fortawesome/free-solid-svg-icons';
import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MatterService from '../../../../../AppSettings/services/MatterService';
import UnifiedTopBar from '@frontend/Components/TopBar/UnifiedTopBar';
import { createDetailViewTemplate } from '@frontend/Components/TopBar/templates';
import Tabs from '@frontend/Components/Tabs';

interface FinancialDetailsTopBarProps {
  setActivePage: (page: string) => void;
  activePage?: string;
  isTimesheetOnlyUser?: boolean;
}

const FinancialDetailsTopBar: React.FC<FinancialDetailsTopBarProps> = ({ 
  setActivePage, 
  activePage = 'dashboard',
  isTimesheetOnlyUser = false
}) => {
  const { matterId } = useParams();
  const navigate = useNavigate();
  const [matterName, setMatterName] = useState<{ mattername: string; sept_tenantid: string; } | null>(null);

  const fetchMatterName = useCallback(async () => {
    try {
      const response = await MatterService.getMatterName(matterId!);
      setMatterName(response ?? null);
    } catch (error) {
      console.error('Error fetching matter name:', error);
      setMatterName(null);
    }
  }, [matterId]);

  useEffect(() => {
    fetchMatterName();
  }, [fetchMatterName]);

  const matterTitle = matterName?.mattername
    ? `${matterId} - ${matterName?.mattername}`
    : 'Loading...';

  // Function to switch to a section
  const switchToSection = (sectionName: string) => {
    setActivePage(sectionName);
  };

  const allDropdownItems = [
    {
      label: 'Dashboard',
      onClick: () => switchToSection('dashboard'),
    },
    {
      label: 'Invoice',
      onClick: () => switchToSection('invoice'),
    },
    {
      label: 'Billing',
      onClick: () => switchToSection('billing'),
    },
    {
      label: 'Estimate',
      onClick: () => switchToSection('estimate'),
    },
    {
      label: 'Expenses',
      onClick: () => switchToSection('expenses'),
    },
    {
      label: 'Transactions',
      onClick: () => switchToSection('transactions'),
    },
  ];

  const dropdownItems = isTimesheetOnlyUser
    ? [{ label: 'Timesheets', onClick: () => switchToSection('billing') }]
    : allDropdownItems;

  const unifiedConfig = createDetailViewTemplate({
    leftExtras: [
      {
        type: 'button',
        icon: faAnglesLeft,
        onClick: () => navigate(`/matterDetails/${matterId}`),
        ariaLabel: 'Back to matter details',
      },
      {
        type: 'text',
        text: matterTitle,
        style: { marginLeft: '10px', fontWeight: 'bold' },
      },
    ],
    dropdown: {
      icon: faBars,
      ariaLabel: 'Matter financial sections',
      items: dropdownItems,
    },
    pageContext: {
      route: 'matters-financial',
      matterId,
      matterName: matterName?.mattername,
    },
  });

  // Map activePage to tab ID - ensure they match
  const currentTabId = activePage;

  const tabs = dropdownItems.map((item) => {
    // Map label to the corresponding activePage value
    const labelToPageMap: Record<string, string> = {
      'Dashboard': 'dashboard',
      'Invoice': 'invoice',
      'Billing': 'billing',
      'Estimate': 'estimate',
      'Expenses': 'expenses',
      'Transactions': 'transactions',
    };
    return {
      id: labelToPageMap[item.label] || item.label.toLowerCase(),
      label: item.label,
      onClick: item.onClick,
    };
  });

  const handleTabChange = (tabId: string) => {
    const match = tabs.find((t) => t.id === tabId);
    if (match) {
      match.onClick();
    }
  };

  // Hide the entire TopBar for timesheet-only users since they only have one view
  if (isTimesheetOnlyUser) {
    return null;
  }

  return (
    <>
      <UnifiedTopBar {...unifiedConfig} />
      <div className="financial-tabs">
        <Tabs
          tabs={tabs.map(({ id, label }) => ({ id, label }))}
          activeTabId={currentTabId}
          onTabChange={handleTabChange}
          variant="underline"
        />
      </div>
    </>
  );
};

export default FinancialDetailsTopBar;
